import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useLanguage, LangToggle } from '../context/LanguageContext'
import { useSubscription } from '../context/SubscriptionContext'

const PLAN_AMOUNTS = { monthly: '9.99', yearly: '29.99' }

export default function Pricing() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const { plan: currentPlan, subscription } = useSubscription()
  const navigate = useNavigate()
  const [selectedPlan, setSelectedPlan] = useState('yearly')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [applePayAvailable, setApplePayAvailable] = useState(false)

  useEffect(() => {
    if (window.ApplePaySession && ApplePaySession.canMakePayments()) {
      setApplePayAvailable(true)
    }
  }, [])

  const handleSubscribe = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await fetch('/api/create-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: selectedPlan,
          user_id: user.id,
          user_email: user.email,
          user_name: user.user_metadata?.full_name || user.user_metadata?.name,
          language: t.dir === 'rtl' ? 'ar' : 'en',
        }),
      })
      const data = await res.json()
      if (data.redirect_url) {
        window.location.href = data.redirect_url
      } else {
        setError(data.error || t.paymentFailed)
        setLoading(false)
      }
    } catch {
      setError(t.paymentFailed)
      setLoading(false)
    }
  }

  const handleApplePay = async () => {
    setLoading(true)
    setError('')

    const amount = PLAN_AMOUNTS[selectedPlan]
    const label = selectedPlan === 'monthly' ? 'Damanati Monthly' : 'Damanati Yearly'

    try {
      const paymentRequest = {
        countryCode: 'SA',
        currencyCode: 'SAR',
        supportedNetworks: ['mada', 'visa', 'masterCard'],
        merchantCapabilities: ['supports3DS'],
        total: { label, amount },
      }

      const session = new ApplePaySession(3, paymentRequest)

      session.onvalidatemerchant = async (event) => {
        try {
          const res = await fetch('/api/applepay-validate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ validationURL: event.validationURL }),
          })
          const merchantSession = await res.json()
          session.completeMerchantValidation(merchantSession)
        } catch {
          session.abort()
          setError(t.paymentFailed)
          setLoading(false)
        }
      }

      session.onpaymentauthorized = async (event) => {
        try {
          const res = await fetch('/api/applepay-payment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              plan: selectedPlan,
              user_id: user.id,
              user_email: user.email,
              user_name: user.user_metadata?.full_name || user.user_metadata?.name,
              payment_token: event.payment.token,
            }),
          })
          const data = await res.json()

          if (data.success) {
            session.completePayment(ApplePaySession.STATUS_SUCCESS)
            navigate(`/payment-result?order_id=${data.order_id}`)
          } else {
            session.completePayment(ApplePaySession.STATUS_FAILURE)
            setError(data.error || t.paymentFailed)
          }
        } catch {
          session.completePayment(ApplePaySession.STATUS_FAILURE)
          setError(t.paymentFailed)
        }
        setLoading(false)
      }

      session.oncancel = () => {
        setLoading(false)
      }

      session.begin()
    } catch {
      setError(t.paymentFailed)
      setLoading(false)
    }
  }

  const isPremium = currentPlan === 'premium'

  return (
    <div className="app-layout">
      <div className="topbar">
        <button className="topbar-btn" onClick={() => navigate('/dashboard')} aria-label="Back">{t.dir === 'rtl' ? '→' : '←'}</button>
        <div className="topbar-logo"><span style={{ color: '#E8610A', fontWeight: 900, fontSize: '1rem' }}>{t.upgradeToPremium}</span></div>
        <LangToggle />
      </div>

      <div className="main-content">
        {isPremium ? (
          <div className="payment-result">
            <div className="payment-result-icon">⭐</div>
            <div className="payment-result-title">{t.alreadyPremium}</div>
            <div className="payment-result-sub">
              {subscription?.end_date && `${t.expiresOn}: ${new Date(subscription.end_date).toLocaleDateString(t.dir === 'rtl' ? 'ar-SA' : 'en-US')}`}
            </div>
            <button className="btn-full" onClick={() => navigate('/dashboard')} style={{ maxWidth: 300 }}>
              {t.dir === 'rtl' ? '→' : '←'} {t.myWarranties || 'Dashboard'}
            </button>
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <h1 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 8 }}>{t.choosePlan}</h1>
              <p style={{ color: 'var(--ash)', fontSize: '0.85rem' }}>{t.choosePlanSub}</p>
            </div>

            <div className="pricing-cards">
              <div
                className={`pricing-card ${selectedPlan === 'monthly' ? 'selected' : ''}`}
                onClick={() => setSelectedPlan('monthly')}
              >
                <div className="pricing-card-name">{t.monthlyPlan}</div>
                <div className="pricing-card-price">9.99</div>
                <div className="pricing-card-period">{t.sarCurrency} {t.perMonth}</div>
              </div>
              <div
                className={`pricing-card ${selectedPlan === 'yearly' ? 'selected' : ''}`}
                onClick={() => setSelectedPlan('yearly')}
              >
                <div className="pricing-card-save">{t.save50}</div>
                <div className="pricing-card-name">{t.yearlyPlan}</div>
                <div className="pricing-card-price">29.99</div>
                <div className="pricing-card-period">{t.sarCurrency} {t.perYear}</div>
              </div>
            </div>

            {/* Free vs Premium Comparison */}
            <div className="pricing-comparison">
              <div className="pricing-features-title">{t.freeVsPremium}</div>
              <div className="comparison-table">
                <div className="comparison-header">
                  <div className="comparison-cell feature-cell">{t.feature}</div>
                  <div className="comparison-cell">{t.freeCol}</div>
                  <div className="comparison-cell premium-cell">{t.premiumCol}</div>
                </div>
                <div className="comparison-row">
                  <div className="comparison-cell feature-cell">{t.warrantiesLimit}</div>
                  <div className="comparison-cell">{t.upTo10}</div>
                  <div className="comparison-cell premium-cell">{t.unlimited}</div>
                </div>
                <div className="comparison-row">
                  <div className="comparison-cell feature-cell">{t.receiptScan}</div>
                  <div className="comparison-cell">{t.basic}</div>
                  <div className="comparison-cell premium-cell">{t.advanced}</div>
                </div>
                <div className="comparison-row">
                  <div className="comparison-cell feature-cell">{t.emailNotif}</div>
                  <div className="comparison-cell">✓</div>
                  <div className="comparison-cell premium-cell">✓</div>
                </div>
                <div className="comparison-row">
                  <div className="comparison-cell feature-cell">{t.docStorageFeature}</div>
                  <div className="comparison-cell">✕</div>
                  <div className="comparison-cell premium-cell">✓</div>
                </div>
                <div className="comparison-row">
                  <div className="comparison-cell feature-cell">{t.prioritySupport}</div>
                  <div className="comparison-cell">✕</div>
                  <div className="comparison-cell premium-cell">✓</div>
                </div>
              </div>
            </div>

            {error && <div className="error-msg">{error}</div>}

            <button className="btn-full" onClick={handleSubscribe} disabled={loading}>
              {loading ? t.processingPayment : t.subscribe}
            </button>

            {applePayAvailable && (
              <button
                className="btn-full"
                onClick={handleApplePay}
                disabled={loading}
                style={{
                  marginTop: 12,
                  background: '#000',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  maxWidth: '100%',
                }}
              >
                <svg width="20" height="24" viewBox="0 0 20 24" fill="white">
                  <path d="M15.25 0c-1.16.08-2.52.78-3.33 1.72-.73.84-1.36 2.14-1.12 3.39 1.26.04 2.56-.68 3.33-1.6.78-.93 1.3-2.2 1.12-3.51zM19.83 8.3c-.11-.13-2.04-1.24-2.06-3.9-.02-2.17 1.77-3.21 1.85-3.26-1.01-1.49-2.58-1.65-3.14-1.68-1.33-.14-2.6.79-3.27.79-.68 0-1.73-.77-2.84-.75-1.46.02-2.81.85-3.56 2.16-1.52 2.63-.39 6.53 1.09 8.67.73 1.05 1.59 2.22 2.72 2.18 1.09-.04 1.5-.71 2.82-.71 1.31 0 1.69.71 2.83.68 1.17-.02 1.92-1.07 2.64-2.12.83-1.21 1.17-2.39 1.19-2.45-.03-.01-2.28-.88-2.27-3.61z"/>
                </svg>
                {t.payWithApplePay || 'Pay with Apple Pay'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
